import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { invMoveCatApi, FM010 } from '../../../services/invMoveCatApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useInvMoveCatForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FM010[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [sign, setSign] = useState('+');
  const [ratio, setRatio] = useState<number>(0);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await invMoveCatApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setCode(current.fm010_01);
      setName(current.fm010_11);
      setSign(current.fm010_12);
      setRatio(current.fm010_21);
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setCode('');
    setName('');
    setSign('+');
    setRatio(0);
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setCode(current.fm010_01);
    setName(current.fm010_11);
    setSign(current.fm010_12);
    setRatio(current.fm010_21);
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setCode(current.fm010_01);
      setName(current.fm010_11);
      setSign(current.fm010_12);
      setRatio(current.fm010_21);
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await invMoveCatApi.create({ fm010_01: code, fm010_11: name, fm010_12: sign, fm010_21: ratio });
        message.success('新增成功');
      } else {
        await invMoveCatApi.update(current!.fm010_01, { fm010_11: name, fm010_12: sign, fm010_21: ratio });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCode = mode === 'add' ? code : current!.fm010_01;
      const idx = data.findIndex((r: FM010) => r.fm010_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Copy
  const doCopy = async (newCode: string) => {
    if (!current) return;
    try {
      await invMoveCatApi.copy({ new_code: newCode, from_code: current.fm010_01 });
      message.success('複製成功');
      const data = await loadRecords();
      const idx = data.findIndex((r: FM010) => r.fm010_01 === newCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '複製失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await invMoveCatApi.delete(current.fm010_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.fm010_01 >= searchText);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode, name, setName, sign, setSign, ratio, setRatio,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doCopy, doDelete, quickSearch,
    loadRecords,
  };
}
